import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, CheckCircle2, MessageCircle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { CONTACT, WHATSAPP_TRAINING_HREF } from "../lib/contact";

const trainingLabels = {
  face_to_face: "Yüz Yüze (Ataşehir)",
  online: "Online (kişiye özel süreç)",
  not_sure: "Emin değilim",
  business: "İşletme danışmanlığı",
};

export default function TrainingApplication() {
  const [sp] = useSearchParams();
  const typeMap = { online: "online", face: "face_to_face", business: "business" };
  const initial = typeMap[sp.get("type")] || "";
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    training_type: initial,
    experience: "",
    goal: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email || !form.training_type || !form.experience || !form.goal) {
      toast.error("Lütfen zorunlu alanları doldurun.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/training-applications", form);
      toast.success("Başvurunuz alındı. En kısa sürede dönüş yapacağız.");
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-5 lg:px-10 py-24 text-center" data-testid="training-application-success">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full" style={{ background: "var(--em-terracotta-light)", color: "var(--em-terracotta-hover)" }}>
          <CheckCircle2 size={26} />
        </div>
        <h1 className="font-serif mt-6 text-3xl lg:text-4xl font-semibold">Başvurunuz alındı.</h1>
        <p className="mt-4 text-[color:var(--em-espresso-soft)]">
          Teşekkür ederiz. Başvurunuzu kısa sürede inceleyip sizinle iletişime geçeceğiz.
          Hemen görüşmek isterseniz WhatsApp hattımız açık.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <a href={WHATSAPP_TRAINING_HREF} target="_blank" rel="noopener noreferrer" className="btn-terracotta inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold" data-testid="success-whatsapp">
            <MessageCircle size={16} /> WhatsApp&apos;tan Yaz
          </a>
          <Link to="/" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-[color:var(--em-espresso)] text-sm font-semibold hover:bg-[color:var(--em-espresso)] hover:text-white transition-colors" data-testid="success-home">
            Anasayfaya Dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-5 lg:px-10 py-14 lg:py-20" data-testid="page-training-application">
      <div className="eyebrow">Eğitim Başvurusu</div>
      <h1 className="font-serif mt-4 text-4xl lg:text-5xl font-semibold leading-[1.08]">Birlikte başlayalım.</h1>
      <p className="mt-4 text-[color:var(--em-espresso-soft)] max-w-2xl">
        Formu doldurduktan sonra, size uygun bir takvim ile en kısa sürede dönüş yapacağız.
      </p>

      <form onSubmit={submit} className="mt-10 grid gap-5 p-6 lg:p-10 rounded-sm border" style={{ borderColor: "var(--em-border)", background: "var(--em-stone)" }}>
        <Field label="Ad Soyad *">
          <input className={inputCls} value={form.full_name} onChange={set("full_name")} required data-testid="field-full-name" />
        </Field>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Telefon (WhatsApp tercihen) *">
            <input className={inputCls} value={form.phone} onChange={set("phone")} required inputMode="tel" placeholder="+90 5XX XXX XX XX" data-testid="field-phone" />
          </Field>
          <Field label="E-posta *">
            <input className={inputCls} type="email" value={form.email} onChange={set("email")} required data-testid="field-email" />
          </Field>
        </div>

        <Field label="Hangi eğitim ilgini çekiyor? *">
          <div className="grid sm:grid-cols-2 gap-3">
            {Object.entries(trainingLabels).map(([k, v]) => (
              <label key={k} className={`cursor-pointer border rounded-sm px-4 py-3 text-sm font-medium flex items-center gap-3 transition-colors ${form.training_type === k ? "border-[color:var(--em-terracotta)] bg-[color:var(--em-terracotta-light)]" : "border-[color:var(--em-border)] bg-white hover:border-[color:var(--em-terracotta)]"}`} data-testid={`field-training-${k}`}>
                <input type="radio" name="training_type" value={k} checked={form.training_type === k} onChange={set("training_type")} className="accent-[color:var(--em-terracotta)]" />
                {v}
              </label>
            ))}
          </div>
        </Field>

        <Field label="Daha önce ekşi mayalı ekmek yaptınız mı? *">
          <textarea rows={3} className={inputCls} value={form.experience} onChange={set("experience")} required placeholder="Ör: Hiç yapmadım / Birkaç kez denedim / Düzenli pişiriyorum..." data-testid="field-experience" />
        </Field>

        <Field label="Hedefin nedir? *">
          <textarea rows={3} className={inputCls} value={form.goal} onChange={set("goal")} required placeholder="Örn: Evimde tutarlı pişim yapmak, küçük bir fırın işletmek..." data-testid="field-goal" />
        </Field>

        <Field label="Ek mesaj (opsiyonel)">
          <textarea rows={3} className={inputCls} value={form.message} onChange={set("message")} data-testid="field-message" />
        </Field>

        <div className="pt-2 flex flex-col sm:flex-row gap-3 items-center">
          <button type="submit" disabled={submitting} className="btn-terracotta inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold disabled:opacity-60" data-testid="submit-training-application">
            <Send size={16} /> {submitting ? "Gönderiliyor..." : "Başvuruyu Gönder"}
          </button>
          <span className="text-xs text-[color:var(--em-text-mute)]">Bilgileriniz yalnızca iletişim amacıyla kullanılır.</span>
        </div>
      </form>
    </div>
  );
}

const inputCls = "w-full border border-[color:var(--em-border)] rounded-sm bg-white px-4 py-3 text-sm text-[color:var(--em-espresso)] placeholder:text-[color:var(--em-text-mute)] focus:outline-none focus:border-[color:var(--em-terracotta)] focus:ring-1 focus:ring-[color:var(--em-terracotta)] transition-colors";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium mb-2 text-[color:var(--em-espresso)]">{label}</span>
      {children}
    </label>
  );
}
